/**
 * MarkDuplicateDialog.tsx — the ACTION half of the duplicate surface
 * (2026-09-06, CTO: "modelled on the Asana flow").
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHAT WAS MISSING. clinic-service has served `POST /v1/deals/:id/mark-duplicate`
 * for a while and duplicateSurface.tsx renders both ends of a merged pair
 * beautifully — a marker, a banner, the canonical's list, the audit trail. None
 * of it could ever appear, because NOTHING IN THIS PORTAL COULD MAKE A
 * DUPLICATE. There was no caller for the route, so the `closed_duplicate` lane
 * had no way in, no way out, and nothing in it: a rendered column that could
 * only ever be empty. This module is the way in and the way out.
 *
 * ⚠️ THE UNDO IS NOT OPTIONAL FURNITURE, IT IS HALF THE FEATURE. Staff pick a
 * canonical out of a list of people with similar names, from a search they
 * typed in a hurry, about a patient they are on the phone to. They WILL
 * mis-pick. A merge with no release is a card stranded off every board — which
 * is exactly the state `MANUAL_TARGETS.closed_duplicate = []` describes, and
 * the reason that lane is deliberately dead-ended in the stage map: the release
 * is this action's revert, not a stage move.
 *
 * ⚠️ AND THE MONEY STAYS VISIBLE. A duplicate that reached checkout carries a
 * REAL CHARGE, and marking one deliberately cascades to NOTHING in
 * orders-service — unlike the test mark, which takes the case off the payments
 * board. The confirm says so in plain words, and the response's `payment` block
 * is surfaced afterwards, because the operator has just hidden a card that may
 * need refunding and "it disappeared" must never be the whole story.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { searchDeals } from '../api/deals';
import type { DealCard } from '../api/types';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, Field, Help, TextInput } from '../ui/kit';
import { formatUtcDate } from '../lib/format';
import { dealName } from './PipelineBoard';
import { STAGE_LABEL } from './stages';

/** The shortest term clinic-service's search accepts — one character is a 400. */
const MIN_TERM = 2;

/**
 * How many candidates the list shows.
 *
 * A CAP, not a page: this is a "find the other card" affordance, not a board.
 * Past a handful of near-identical rows a person stops reading and starts
 * guessing, and guessing is the failure mode the undo exists for — so a term
 * that matches half the tenant is answered with "narrow it down", never with a
 * scroll of forty faces to pick a stranger out of.
 */
const MAX_CANDIDATES = 8;

/**
 * The refusals clinic-service makes, rendered as sentences somebody can ACT on.
 *
 * ⚠️ THE SERVER'S MESSAGES ARE MOSTLY ALREADY THAT, AND THEY PASS THROUGH
 * VERBATIM — "This deal is the canonical for 2 other duplicate(s) — un-mark
 * those first" tells an operator precisely what to go and do, and rewriting it
 * here would fork the wording the day the server's rule moves. What this
 * function adds is the two TERSE ones. "Unknown canonical deal" is a 404 whose
 * cause is almost always "the card you picked was deleted or belongs to another
 * clinic", and a bare four-letter status is the difference between a person
 * retrying usefully and a person filing a bug.
 *
 * R19: every branch is generic copy or a PHI-free server sentence. Nothing from
 * a patient's record is interpolated.
 */
export function duplicateRefusal(detail: string | undefined, reverting: boolean): string {
  if (detail && /unknown canonical/i.test(detail)) {
    return 'We could not find that deal any more — it may have been removed, or it belongs to a different clinic. Search again and pick another.';
  }
  if (detail && /unknown deal/i.test(detail)) {
    return 'This deal could not be found. Close the drawer and reload the board.';
  }
  // Self-reference, the loop 409, the dependents 409 and the test-record 409
  // all arrive as complete, actionable sentences. Say them.
  if (detail) return detail;
  return reverting
    ? 'Sorry — we could not release this duplicate. Please try again.'
    : 'Sorry — we could not mark this deal as a duplicate. Please try again.';
}

/**
 * ONE candidate row. A radio, not a button, and that is an accessibility
 * decision rather than a stylistic one: the list is a single choice out of
 * several, screen-reader users get "2 of 6" for free, and arrow keys move
 * through it the way a person expects a picker to behave.
 */
function CandidateRow({
  card,
  selected,
  onSelect,
}: {
  card: DealCard;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <label
        style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          padding: '8px 10px',
          minHeight: '44px',
          borderRadius: 'var(--gps-r-md)',
          border: `1px solid ${selected ? 'var(--gps-accent)' : 'var(--gps-border)'}`,
          background: selected ? 'var(--gps-surface-2)' : 'transparent',
          cursor: 'pointer',
        }}
      >
        <input
          type="radio"
          name="canonical-deal"
          checked={selected}
          onChange={onSelect}
          style={{ marginTop: '3px' }}
        />
        <span style={{ display: 'block', minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 600, fontSize: '14px' }}>{dealName(card)}</span>
          {/* The email is the identity contacts key on, and it is the field
              that actually distinguishes two people with the same name — which
              is the whole job of this list. */}
          <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--gps-muted)', wordBreak: 'break-all' }}>
            {card.contact.email}
          </span>
          <span style={{ display: 'block', fontSize: '12px', color: 'var(--gps-muted)' }}>
            {STAGE_LABEL[card.stage] ?? card.stage} · started {formatUtcDate(card.createdAt)}
          </span>
        </span>
      </label>
    </li>
  );
}

/**
 * "Mark as duplicate of…" — the picker and the confirm, in one dialog.
 *
 * It is a `ConfirmDialog` rather than a bespoke modal so it inherits the
 * grammar every other destructive-ish action on this drawer uses (the reason
 * field, the busy lock, the escape/cancel behaviour) — and `confirmDisabled`
 * carries the one extra gate this action needs: no canonical picked, no
 * confirm. A dialog that let you press "Mark as duplicate" with nothing
 * selected would send a body the server refuses.
 */
export function MarkDuplicateDialog({
  deal,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  /** The card being marked — excluded from its own candidate list. */
  deal: DealCard;
  busy: boolean;
  /** A server refusal, already through `duplicateRefusal`. */
  error: string | null;
  onCancel: () => void;
  onConfirm: (canonicalDealId: string, reason?: string) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<DealCard[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [selected, setSelected] = useState<DealCard | null>(null);
  /**
   * Stamped at request time and compared on arrival — a slow response for an
   * earlier term must never overwrite the results of a later one. The board's
   * `queryGeneration` pattern, in miniature.
   */
  const generation = useRef(0);

  // 300ms, the same pause the board's search uses: shorter and an average
  // typist fires a request per character, longer and the list visibly lags
  // behind a finished word.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  const runSearch = useCallback((q: string) => {
    if (q.length < MIN_TERM) {
      setResults(null);
      setSearching(false);
      setSearchFailed(false);
      return;
    }
    const mine = (generation.current += 1);
    setSearching(true);
    setSearchFailed(false);
    /**
     * ⚠️ `includeDuplicates: true`, DELIBERATELY, AND IT IS NOT AN OVERSHARE.
     * A card that is ALREADY merged is a legitimate thing to find here: the
     * server resolves one hop through it, so pointing at a duplicate quietly
     * points at its canonical. Hiding them would have let staff pick a card the
     * server then refuses to explain — for a row the portal could see and chose
     * not to show. Test records stay excluded (the third argument is omitted):
     * merging a real patient into a rehearsal is a refusal the server makes for
     * good reason, and offering it as a choice invites it.
     */
    searchDeals(q, undefined, undefined, true)
      .then((list) => {
        if (generation.current !== mine) return;
        setResults(list.items);
        setSearching(false);
      })
      .catch(() => {
        if (generation.current !== mine) return;
        setResults(null);
        setSearching(false);
        setSearchFailed(true);
      });
  }, []);

  useEffect(() => {
    runSearch(debounced);
  }, [debounced, runSearch]);

  // The card being marked can never be its own canonical (the server 400s it),
  // so it is removed from the list rather than offered and refused.
  const candidates = (results ?? []).filter((c) => c.dealId !== deal.dealId);
  const shown = candidates.slice(0, MAX_CANDIDATES);
  const overflowed = candidates.length > shown.length;

  return (
    <ConfirmDialog
      open
      title={`Mark ${dealName(deal)} as a duplicate`}
      confirmLabel={busy ? 'Merging…' : 'Mark as duplicate'}
      busy={busy}
      // No canonical, no confirm — see the component docblock.
      confirmDisabled={selected === null}
      optionalReason
      reasonLabel="Why is this a duplicate? (optional)"
      onCancel={onCancel}
      onConfirm={(reason) => {
        if (selected) onConfirm(selected.dealId, reason);
      }}
      body={
        <>
          <Field
            label="Find the deal this one is a copy of"
            htmlFor="canonical-search"
            hint="Search by name or email — at least 2 characters."
          >
            <TextInput
              id="canonical-search"
              type="search"
              autoComplete="off"
              value={term}
              placeholder="Name or email"
              onChange={(e) => {
                setTerm(e.target.value);
                // A new search invalidates the old pick: the selected card may
                // not even be in the next list, and confirming a selection the
                // operator can no longer see is the mis-merge this dialog is
                // built to avoid.
                setSelected(null);
              }}
            />
          </Field>

          {/* Live region: the list changes under a text field the user is
              still typing in, and a sighted user sees that happen. */}
          <div role="status" aria-live="polite" style={{ marginBottom: 'var(--gps-s-2)' }}>
            {searching ? (
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--gps-muted)' }}>Searching…</p>
            ) : null}
            {searchFailed ? (
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--gps-danger-text)' }}>
                The search did not run. Try again, or close and reload the board.
              </p>
            ) : null}
            {!searching && !searchFailed && results !== null && shown.length === 0 ? (
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--gps-muted)' }}>
                No other deals match that. Try their email address, or a shorter name.
              </p>
            ) : null}
          </div>

          {shown.length > 0 ? (
            <ul
              aria-label="Matching deals"
              style={{ listStyle: 'none', margin: '0 0 var(--gps-s-2)', padding: 0, display: 'grid', gap: '6px' }}
            >
              {shown.map((card) => (
                <CandidateRow
                  key={card.dealId}
                  card={card}
                  selected={selected?.dealId === card.dealId}
                  onSelect={() => setSelected(card)}
                />
              ))}
            </ul>
          ) : null}

          {overflowed ? (
            <Help style={{ marginBottom: 'var(--gps-s-2)' }}>
              More than {MAX_CANDIDATES} deals match. Narrow the search before picking one — merging the
              wrong pair is easy to do and has to be undone by hand.
            </Help>
          ) : null}

          {/* ⚠️ WHAT THIS ACTUALLY DOES, IN THREE SENTENCES, AND THE THIRD ONE
              IS THE IMPORTANT ONE. An operator who reads only "the card
              disappears" will assume the money went with it, the way it does
              for a test record. It does not, and it must not — a patient who
              submitted twice may have PAID twice, and that charge is the thing
              somebody has to go and refund. */}
          <p style={{ margin: '0 0 8px' }}>
            This closes {dealName(deal)} as a duplicate and takes it off the pipeline board. Both records
            link to each other, so either one opens the other, and nothing is deleted.
          </p>
          <p style={{ margin: '0 0 8px' }}>
            <strong>Any payment on this deal stays exactly where it is</strong> — on the Payments board,
            counting as real money. If this patient was charged twice, that charge still needs refunding.
          </p>
          <p style={{ margin: '0 0 8px', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
            Picked the wrong one? Open the card again and choose &ldquo;Release from duplicate&rdquo; — it
            goes back to the lane it came from.
          </p>

          {error ? (
            <Alert tone="danger" role="alert" style={{ marginBottom: 0 }}>
              {error}
            </Alert>
          ) : null}
        </>
      }
    />
  );
}

/**
 * "Release from duplicate" — the undo.
 *
 * A GENUINE RESTORE, unlike the test-record un-mark, and the copy has to say
 * which: clinic-service banks the lane the card came from at mark time and puts
 * it back there. Saying "it returns to the board" would be an overclaim for the
 * test mark and is the literal truth here, so it is said plainly.
 */
export function ReleaseDuplicateDialog({
  deal,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  deal: DealCard;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason?: string) => void;
}) {
  return (
    <ConfirmDialog
      open
      title={`Release ${dealName(deal)} from its duplicate`}
      confirmLabel={busy ? 'Releasing…' : 'Release from duplicate'}
      busy={busy}
      optionalReason
      reasonLabel="Why is this not a duplicate? (optional)"
      onCancel={onCancel}
      onConfirm={(reason) => onConfirm(reason)}
      body={
        <>
          <p style={{ margin: '0 0 8px' }}>
            This unlinks the two records and puts this card back on the pipeline, in the lane it was in
            when it was merged. The other deal keeps everything it had.
          </p>
          <p style={{ margin: '0 0 8px', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
            Both cards&rsquo; activity trails keep the record that they were merged and then released.
          </p>
          {error ? (
            <Alert tone="danger" role="alert" style={{ marginBottom: 0 }}>
              {error}
            </Alert>
          ) : null}
        </>
      }
    />
  );
}
